import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import { friendlyMessage } from '@/lib/errors';
import {
  Button, PageShell, SectionCard, FormSection, Input, RadixSelect, Switch, Skeleton,
  RequiredMark,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
} from '@/components/ui';
import {
  usePolicyQuery,
  usePolicyMutation,
  useActivePoliciesByType,
} from '../hooks/usePoliciesQuery';
import {
  POLICY_TYPES,
  type Policy,
  type PolicyCreatePayload,
  type PolicyType,
  type PolicyUpdatePayload,
} from '../types/policy';
import {
  needsSingleActiveConfirm,
  policiesToDeactivate,
} from '../lib/singleActivePolicy';
import { LocaleHtmlTabs } from '@/features/cms/components/LocaleHtmlTabs';
import type { ContentLocale } from '@/features/cms/lib/contentLocales';
import {
  buildPolicyTranslations,
  pickPolicyTranslations,
  type LocaleTextMap,
} from '../lib/policyTranslations';

interface PolicyFormState {
  type: PolicyType;
  name: string;
  content: string;
  isActive: boolean;
  /** 해외몰(en-US/ja-JP/zh-CN) 정책명·문구. 비우면 국문 정본으로 폴백한다(catalog V127). */
  nameTranslations: LocaleTextMap;
  contentTranslations: LocaleTextMap;
}

const EMPTY_FORM: PolicyFormState = {
  type: 'SHIPPING',
  name: '',
  content: '',
  isActive: true,
  nameTranslations: {},
  contentTranslations: {},
};

export const PolicyFormPage = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { policyId: policyIdParam } = useParams<{ policyId: string }>();
  const isNew = policyIdParam === 'new' || policyIdParam == null;
  const policyId = isNew ? null : Number(policyIdParam);

  const query = usePolicyQuery(policyId);
  const { create, update, remove } = usePolicyMutation();

  const [form, setForm] = useState<PolicyFormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // 단일활성 모달이 가리키는 기존 활성 정책들(같은 type). 비어있지 않으면 모달 표시.
  const [conflictActive, setConflictActive] = useState<ReadonlyArray<Policy> | null>(null);

  // G-1: 신규 생성(isActive=true) 시 같은 type 의 기존 활성 정책 단일활성 보장용.
  const activePolicies = useActivePoliciesByType(isNew ? form.type : undefined);

  useEffect(() => {
    if (!query.data) return;
    setForm({
      type: query.data.type,
      name: query.data.name,
      content: query.data.content,
      isActive: query.data.isActive,
      ...pickPolicyTranslations(query.data.translations),
    });
  }, [query.data]);

  const set = <K extends keyof PolicyFormState>(key: K, value: PolicyFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const setTranslation = (
    key: 'nameTranslations' | 'contentTranslations',
    locale: ContentLocale,
    value: string,
  ) => setForm((prev) => ({ ...prev, [key]: { ...prev[key], [locale]: value } }));

  const typeOptions = POLICY_TYPES.map((type) => ({ value: type, label: t(`policy.type.${type}`) }));

  const canSubmit = form.name.trim() !== '' && form.content.trim() !== '';

  /** 신규 정책 생성. deactivatePrior=true 면 같은 type 의 기존 활성 정책을 먼저 비활성화. */
  const runCreate = async (deactivatePrior: boolean) => {
    setError(null);
    setConflictActive(null);
    const translations = buildPolicyTranslations(form);
    const payload: PolicyCreatePayload = {
      type: form.type,
      name: form.name.trim(),
      content: form.content,
      isActive: form.isActive,
      ...(translations ? { translations } : {}),
    };
    try {
      if (deactivatePrior) {
        // 단일활성 보장: 기존 활성 정책들을 isActive=false 로 전환한 뒤 새 정책 생성.
        const priors = policiesToDeactivate(form.type, activePolicies.data?.items ?? []);
        for (const prior of priors) {
          await update.mutateAsync({
            policyId: prior.id,
            payload: {
              type: prior.type,
              name: prior.name,
              content: prior.content,
              isActive: false,
            },
          });
        }
      }
      await create.mutateAsync(payload);
      navigate('/policies');
    } catch (e) {
      setError(friendlyMessage(e, t('policy.saveFailed')));
    }
  };

  const handleSubmit = async () => {
    setError(null);
    if (!canSubmit) {
      setError(t('policy.validation.required'));
      return;
    }

    if (isNew) {
      // G-1: 같은 type 의 기존 활성 정책이 있고 새 정책도 활성이면 단일활성 모달로 의사 확인.
      const priors = policiesToDeactivate(form.type, activePolicies.data?.items ?? []);
      if (
        needsSingleActiveConfirm(
          { isNew, isActive: form.isActive, type: form.type },
          priors,
        )
      ) {
        setConflictActive(priors);
        return;
      }
      await runCreate(false);
      return;
    }

    if (policyId != null) {
      // 불러온 번역을 함께 넘겨, 이번에 비운 로케일은 빈 값으로 보내 서버 번역을 지운다(국문 폴백).
      const translations = buildPolicyTranslations(form, query.data?.translations);
      const payload: PolicyUpdatePayload = {
        type: form.type,
        name: form.name.trim(),
        content: form.content,
        isActive: form.isActive,
        ...(translations ? { translations } : {}),
      };
      try {
        await update.mutateAsync({ policyId, payload });
        navigate('/policies');
      } catch (e) {
        setError(friendlyMessage(e, t('policy.saveFailed')));
      }
    }
  };

  const handleDelete = async () => {
    if (policyId == null) return;
    setError(null);
    try {
      await remove.mutateAsync(policyId);
      navigate('/policies');
    } catch (e) {
      setError(friendlyMessage(e, t('policy.deleteFailed')));
    }
  };

  const isBusy = create.isPending || update.isPending || remove.isPending;

  const breadcrumb = [
    { label: t('policy.title'), href: '/policies' },
    { label: isNew ? t('policy.create') : t('policy.edit') },
  ];

  if (!isNew && query.isPending) {
    return (
      <PageShell title={t('policy.editTitle')} breadcrumb={breadcrumb}>
        <div className="space-y-3" role="status" aria-live="polite">
          <Skeleton className="h-[44px] w-full" />
          <Skeleton className="h-[200px] w-full" />
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      title={isNew ? t('policy.createTitle') : t('policy.editTitle')}
      breadcrumb={breadcrumb}
      actions={
        <div className="flex gap-2">
          {!isNew && (
            <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)} disabled={isBusy}>
              {t('policy.delete')}
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => navigate('/policies')} disabled={isBusy}>
            {t('policy.cancel')}
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={isBusy || !canSubmit}>
            {t('policy.save')}
          </Button>
        </div>
      }
    >
      {error && (
        <div className="rounded-md p-3 text-sm" style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}>
          {error}
        </div>
      )}

      <SectionCard title={t('policy.sectionBasic')}>
        <div className="flex flex-col gap-3">
          <FormSection label={t('policy.fields.type')} density="compact">
            {isNew ? (
              <div className="max-w-[200px]">
                <RadixSelect
                  value={form.type}
                  onChange={(v) => set('type', v as PolicyType)}
                  options={typeOptions}
                  aria-label={t('policy.fields.type')}
                />
              </div>
            ) : (
              <Input value={t(`policy.type.${form.type}`)} readOnly className="max-w-[200px]" />
            )}
          </FormSection>
          <FormSection label={t('policy.fields.name')} density="compact" required>
            <Input
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              maxLength={100}
              required
            />
          </FormSection>
          <FormSection label={t('policy.fields.isActive')} density="compact">
            <Switch
              checked={form.isActive}
              onCheckedChange={(v) => set('isActive', v)}
              aria-label={t('policy.fields.isActive')}
            />
          </FormSection>
        </div>
      </SectionCard>

      {/* QA-56 — 약관 본문은 저장 검증(canSubmit)이 하드 강제하므로 필수 표기. */}
      <SectionCard title={<span>{t('policy.sectionContent')}<RequiredMark /></span>}>
        {/* catalog V127 — 해외몰(영·일·중) 정책명·문구. 국문이 정본이고, 비운 로케일은 국문으로 폴백한다.
            해외몰 PDP 배송안내·이용안내와 공개 정책 페이지가 쓴다. 한국어 탭의 이름은 위 정책명과 같은 값이다. */}
        <LocaleHtmlTabs
          baseValue={form.content}
          onBaseChange={(html) => set('content', html)}
          translations={form.contentTranslations}
          onTranslationChange={(locale, html) => setTranslation('contentTranslations', locale, html)}
          titleField={{
            label: t('policy.fields.name'),
            baseValue: form.name,
            onBaseChange: (v) => set('name', v),
            translations: form.nameTranslations,
            onTranslationChange: (locale, v) => setTranslation('nameTranslations', locale, v),
            placeholder: t('policy.fields.nameTranslationPlaceholder', {
              defaultValue: '비우면 국문 정책명을 씁니다',
            }),
            maxLength: 100,
          }}
          ariaLabelPrefix={t('policy.fields.content')}
        />
      </SectionCard>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('policy.deleteTitle')}</DialogTitle>
            <DialogDescription>
              {t('policy.deleteDesc', { name: form.name })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">{t('policy.cancel')}</Button>
            </DialogClose>
            <Button
              variant="danger"
              size="sm"
              disabled={isBusy}
              onClick={() => { setConfirmDelete(false); void handleDelete(); }}
            >
              {t('policy.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={conflictActive != null}
        onOpenChange={(o) => { if (!o) setConflictActive(null); }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('policy.singleActive.title')}</DialogTitle>
            <DialogDescription>
              {t('policy.singleActive.desc', { type: t(`policy.type.${form.type}`) })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="ghost"
              size="sm"
              disabled={isBusy}
              onClick={() => { void runCreate(false); }}
            >
              {t('policy.singleActive.keepBoth')}
            </Button>
            <Button
              size="sm"
              disabled={isBusy}
              onClick={() => { void runCreate(true); }}
            >
              {t('policy.singleActive.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};
